import { useParams, Link, Navigate } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { integrations, getIntegrationBySlug } from '@/content/integrations';
import { NotifyForm } from '@/components/NotifyForm';
import { useSeo } from '@/seo/useSeo';

export function IntegrationDetail(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const integ = getIntegrationBySlug(slug);

  useSeo({
    title: integ ? `${integ.name} integration` : 'Integration not found',
    description: integ ? integ.summary : 'This integration page could not be found.',
    path: `/integrations/${slug ?? ''}`,
  });

  if (!integ) return <Navigate to="/integrations" replace />;

  const Icon = integ.icon;
  const related = integrations.filter((i) => i.slug !== integ.slug).slice(0, 3);

  return (
    <div>
      <section className="relative overflow-hidden pt-16 pb-16 md:pt-24 md:pb-24">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-nexus-grid bg-grid [mask-image:radial-gradient(circle_at_50%_0%,#000,transparent_70%)]"
        />
        <div className="container-content relative max-w-[760px]">
          <p className="text-xs text-white/40">
            <Link to="/integrations" className="text-white/40 no-underline hover:text-nexus-purple-light">
              Integrations
            </Link>{' '}
            / {integ.name}
          </p>
          <div className="mb-5 mt-6 flex h-12 w-12 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
            <Icon fontSize="medium" />
          </div>
          <div className="mb-4">
            <Chip
              label={integ.category}
              size="small"
              className="h-[22px] border border-nexus-purple/35 bg-transparent text-[10.5px] font-bold tracking-wide text-nexus-purple-light"
            />
          </div>
          <h1 className="font-display text-4xl font-bold leading-[1.1] tracking-tight text-nexus-ink md:text-[44px]">
            {integ.name}
          </h1>
          <p className="mt-5 text-lg text-white/70">{integ.summary}</p>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">How it works</h2>
          <ul className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
            {integ.details.map((d) => (
              <li key={d} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <CheckCircleOutlineIcon fontSize="small" className="mt-0.5 flex-shrink-0 text-nexus-purple-light" />
                <span className="text-sm text-white/75">{d}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-xs text-white/40">{integ.statusNote}</p>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">Other integrations</h2>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {related.map((i) => {
              const RelIcon = i.icon;
              return (
                <Link
                  key={i.slug}
                  to={`/integrations/${i.slug}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 no-underline transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50"
                >
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
                    <RelIcon fontSize="small" />
                  </div>
                  <h3 className="font-display text-[15px] font-semibold text-nexus-ink">{i.name}</h3>
                  <p className="mt-1.5 text-sm text-white/65">{i.summary}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
            <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
              See {integ.name} inside Nexus.
            </h2>
            <p className="mx-auto mt-3 max-w-[520px] text-white/70">
              Join the design-partner cohort and we&apos;ll show you exactly how your existing stack
              folds into the console.
            </p>
            <div className="mt-8 flex justify-center">
              <NotifyForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
